import { AppNavParams } from '@euler/app/Routes';
import { ParamListBase, useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { ReactNode, createContext, memo, useContext, useMemo } from 'react';

type AppNavigationParams = AppNavParams;

export const AppNavigationContext = createContext<{
  navigation: StackNavigationProp<AppNavigationParams>;
}>(null as any);

export const AppNavigationProvider = memo((props: { children: ReactNode }) => {
  const parentAppNav = useContext(AppNavigationContext)?.navigation;
  const currentNav = useNavigation<StackNavigationProp<AppNavigationParams>>();
  const navigation = parentAppNav ?? currentNav;
  const context = useMemo(() => ({ navigation }), [navigation]);
  return (
    <AppNavigationContext.Provider value={context}>
      {props.children}
    </AppNavigationContext.Provider>
  );
});

export const useAppNavigation = <
  T extends ParamListBase = AppNavigationParams,
>() => {
  const rootNav = useContext(AppNavigationContext)
    ?.navigation as StackNavigationProp<T>;
  const navigation = useNavigation<StackNavigationProp<T>>();
  const nav = rootNav ?? navigation;
  return useMemo(() => {
    let curr = nav;
    while (curr?.getParent() != null) {
      curr = curr.getParent();
    }
    return curr;
  }, [nav]);
};

export type AppNavigation = ReturnType<typeof useAppNavigation>;
